import type { EntitlementFailure } from '@/lib/entitlement-failure';
import { create } from 'zustand';
import { materialsApi } from '@/lib/api/materials';
import { subjectModelsApi } from '@/lib/api/subject-models';
import { runSoulSaveJob, type SoulSaveJob, type SoulSaveStage } from '@/lib/soul-save-job';
import { addSoulSaveJob, deleteSoulSaveJob, listSoulSaveJobs, putSoulSaveJob } from '@/lib/soul-save-storage';
import type { PendingAnchorItem } from '@/lib/subject-model-photo-upload';
import { useAuthStore } from './authStore';
import { useSubjectModelsStore } from './subjectModelsStore';

export interface SoulSaveSummary {
  id: string; name: string; stage: SoulSaveStage; saved: number; total: number; errorKey?: string;
  createdAt?: number;
  entitlementFailure?: EntitlementFailure;
  recoverable?: boolean;
}
interface SoulSaveState {
  ownerId: string | null;
  jobs: SoulSaveSummary[];
  busy: boolean;
  error: string | null;
}
export const useSoulSaveStore = create<SoulSaveState>(() => ({ ownerId: null, jobs: [], busy: false, error: null }));

function assertOwner(ownerId: string, signal?: AbortSignal) {
  const auth = useAuthStore.getState();
  if (signal?.aborted || !auth.isAuthenticated || !auth.accessToken || auth.user?.id !== ownerId) throw new Error('AUTH_SESSION_CHANGED');
}
function requireLocks() {
  if (!navigator.locks) throw new Error('soulSaveUnavailable');
  return navigator.locks;
}
function summary(job: SoulSaveJob): SoulSaveSummary {
  return { id: job.id, name: job.name, stage: job.stage, saved: job.photos.filter((photo) => photo.receipt).length,
    total: job.photos.length, errorKey: job.errorKey, createdAt: job.createdAt, entitlementFailure: job.entitlementFailure, recoverable: job.recoverable };
}
function publish(ownerId: string, jobs: SoulSaveSummary[]) {
  if (useAuthStore.getState().user?.id !== ownerId) return;
  useSoulSaveStore.setState({ ownerId, jobs });
}

/** Persist the full bank before dismissing the editor. No paid calls here. */
export async function enqueueSoulSave(id: string, name: string, credits: number, anchors: PendingAnchorItem[]): Promise<void> {
  requireLocks();
  const ownerId = useAuthStore.getState().user?.id;
  if (!ownerId) throw new Error('soulSaveUnavailable');
  assertOwner(ownerId);
  const job: SoulSaveJob = { protocol: 'server_save_v1', id, ownerId, name, credits, createdAt: Date.now(), stage: 'uploading',
    photos: anchors.map((anchor) => ({
      id: anchor.id, blob: anchor.file, filename: anchor.file?.name, remoteUrl: anchor.remoteUrl,
      receipt: !anchor.file && anchor.remoteUrl && anchor.assetId
        ? { url: anchor.remoteUrl, asset_id: anchor.assetId, width: anchor.width, height: anchor.height } : undefined,
    })),
  };
  await addSoulSaveJob(job);
  assertOwner(ownerId);
  const current = useSoulSaveStore.getState();
  publish(ownerId, [summary(job), ...(current.ownerId === ownerId ? current.jobs.filter((item) => item.id !== id) : [])]);
  window.dispatchEvent(new Event('dobidobi:soul-save-enqueued'));
}

/** A cross-tab lock covers loading the current checkpoint AND executing it. */
export async function processSoulSaves(ownerId: string, signal: AbortSignal, resumeId?: string, acceptedCredits?: number): Promise<void> {
  let acquired = false;
  try {
    assertOwner(ownerId, signal);
    await requireLocks().request(`dobidobi:soul-save:${ownerId}`, { ifAvailable: true }, async (lock) => {
      if (!lock) { if (acceptedCredits !== undefined) throw new Error('soulSaveUnavailable'); return; }
      acquired = true;
      assertOwner(ownerId, signal);
      useSoulSaveStore.setState({ ownerId, busy: true, error: null });
      const jobs = await listSoulSaveJobs(ownerId);
      assertOwner(ownerId, signal);
      publish(ownerId, jobs.map(summary));
      for (const job of jobs) {
        if (resumeId && job.id !== resumeId) continue;
        await runSoulSaveJob(job, {
          assertOwner: () => assertOwner(ownerId, signal),
          persist: async (value) => {
            await putSoulSaveJob(value);
            const current = useSoulSaveStore.getState();
            const loadedIds = new Set(jobs.map((item) => item.id));
            publish(ownerId, [...jobs.map(summary), ...(current.ownerId === ownerId
              ? current.jobs.filter((item) => !loadedIds.has(item.id)) : [])]);
          },
          confirm: async (checkpoint) => {
            const response = await materialsApi.confirmUpload(checkpoint, signal);
            if (response.success === false || response.error || !response.data?.url || !response.data.id) return { success: false, error: response.error };
            return { success: true, data: { asset_id: response.data.id, url: response.data.url,
              width: response.data.width, height: response.data.height } };
          },
          upload: async (photo, onCheckpoint) => {
            const response = photo.blob
              ? await materialsApi.uploadMaterial(new File([photo.blob], photo.filename || 'portrait.jpg', { type: photo.blob.type }), 'general', {
                displayName: photo.filename, tags: ['character-model-anchor'], assetCategory: 'project_asset', signal, onCheckpoint,
              })
              : await materialsApi.importFromUrl(photo.remoteUrl!, 'user_material', 'canvas_anchor', { defaultCollectionSlug: 'character' });
            assertOwner(ownerId, signal);
            const asset = response.data && ('asset' in response.data ? response.data.asset : response.data);
            if (response.success === false || response.error || !asset?.id || !asset.url) throw new Error('subjectModelUploadFailed');
            return { asset_id: asset.id, url: asset.url, width: asset.width, height: asset.height };
          },
          create: (value) => subjectModelsApi.createSoul({ name: value.name, request_id: value.id,
            accepted_credits: value.credits, input_images: value.photos.map((photo) => photo.receipt!) }),
          find: (id) => subjectModelsApi.findSoulRequest(id),
          server: {
            ensure: (value) => subjectModelsApi.startSoulSave({ request_id: value.id, name: value.name, accepted_credits: value.credits,
              photos: value.photos.map((photo) => ({ photo_id: photo.id, asset_id: photo.receipt?.asset_id })) }),
            register: (id, index, assetId, checkpoint) => subjectModelsApi.registerSoulSavePhoto(id, index, assetId, checkpoint),
            get: (id) => subjectModelsApi.getSoulSave(id),
            resume: (id, credits) => subjectModelsApi.resumeSoulSave(id, credits),
          },
          onModel: (model) => {
            assertOwner(ownerId, signal);
            useSubjectModelsStore.getState().registerSavedModel(model);
          },
        }, job.id === resumeId, job.id === resumeId ? acceptedCredits : undefined);
      }
    });
  } catch {
    if (!signal.aborted && useAuthStore.getState().user?.id === ownerId && useSoulSaveStore.getState().jobs.length) {
      useSoulSaveStore.setState({ error: 'soulSaveUnavailable' });
    }
  } finally {
    if (acquired && useSoulSaveStore.getState().ownerId === ownerId) useSoulSaveStore.setState({ busy: false });
  }
}

export async function discardSoulSave(ownerId: string, id: string): Promise<void> {
  assertOwner(ownerId);
  await requireLocks().request(`dobidobi:soul-save:${ownerId}`, { ifAvailable: true }, async (lock) => {
    if (!lock) return;
    const jobs = await listSoulSaveJobs(ownerId);
    assertOwner(ownerId);
    const job = jobs.find((item) => item.id === id);
    // Never delete an ambiguous admission receipt or an in-flight upload.
    if (!job || !['upload_failed', 'rejected', 'completed'].includes(job.stage)) return;
    if (job.protocol === 'server_save_v1' && job.stage !== 'completed') {
      const response = await subjectModelsApi.cancelSoulSave(id);
      assertOwner(ownerId);
      if (response.success === false || response.error || !response.data || response.data.stage !== 'cancelled') throw new Error('soulSaveUnavailable');
    }
    await deleteSoulSaveJob(id);
    publish(ownerId, jobs.filter((item) => item.id !== id).map(summary));
  });
}
